import React, { useState, useEffect, useRef } from "react";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import HttpService from "../services/httpService";
import { connect } from "react-redux";
import { setLoading, setSnackInfo } from "../redux/actions/appAction";
import {
	FormControlLabel,
	RadioGroup,
	FormControl,
	Radio,
	Button,
    TextField,
	Grid,
} from "@material-ui/core";
import { CSVLink } from "react-csv";
import XLSX from "xlsx";
import moment from "moment";
import { poFirstApproverName } from "../utils";

const useStyles = makeStyles((theme) => ({
	table: {
		minWidth: 850,
	},
	paper: {
		padding: theme.spacing(2),
		margin: "auto",
		maxWidth: 500,
	},
	report: {
		marginBottom: 20,
		alignItems: "center",
		justifyContent: "center",
	},
    date: {
		marginTop: 10,
		marginBottom: 10,
		paddingTop: 10,
		paddingBottom: 10,
	},
}));

const StyledTableCell = withStyles((theme) => ({
	head: {
		backgroundColor: "#3F51B5",
		color: "#FFF",
		fontWeight: "1000",
	},
	body: {
		fontSize: 14,
	},
}))(TableCell);

const StyledTableRow = withStyles((theme) => ({
	root: {
		"&:nth-of-type(odd)": {
			backgroundColor: theme.palette.action.hover,
		},
	},
}))(TableRow);

const Reports = (props) => {
	const classes = useStyles();
	const [poSoApproved, setPoSoApproved] = useState([]);
	const [radioValue, setRadioValue] = useState("Both");
	const csvButtonRef = useRef(null);
	const [csvData, setCsvData] = useState([]);
    const [startDate, setStartDate] = useState("");
	const [endDate, setEndDate] = useState("");
	const [displayData, setDisplayData] = useState([]);

	// Header for csv file
    const sheetHeader = [
		"Project No.",
		"SO/PO.",
		"Material Name",
		"Requester Name",
		"First Approver Name",
		"Final Approver Name",
		"Vendor Name",
		"Requested Qty",
		"Requested Rate",
		"Requested Value",
		"Standard Qty",
		"Standard Rate",
		"Standard Value",
		"Approved Qty",
		"Approved Rate",
		"Approve Value",
		"Impact Qty",
		"Impact Rate",
		"Impact Value",
		"Approval Date",
	];

	const headers = [
		{ key: "projectCode", label: "Project No." },
		{ key: "projectType", label: "SO/PO." },
		{ key: "itemName", label: "Material Name" },
        { key: "requesterName", label: "Requester Name" },
		{ key: "vendorName", label: "Vendor Name" },
		{ key: "originallyRequestedQty", label: "Requested Qty" },
		{ key: "originallyRequestedRate", label: "Requested Rate" },
		{ key: "originallyRequestedValue", label: "Requested Value" },
		{ key: "standardQuantity", label: "Standard Qty" },
		{ key: "standardRate", label: "Standard Rate" },
		{ key: "standardValue", label: "Standard Value" },
		{ key: "approvedQuantity", label: "Approved Qty" },
		{ key: "approvedRate", label: "Approved Rate" },
		{ key: "approvedValue", label: "Approve Value" },
		{ key: "impactQuantity", label: "Impact Qty" },
		{ key: "impactRate", label: "Impact Rate" },
		{ key: "impactValue", label: "Impact Value" },
        { key: "updatedAt", label: "Approval Date" },
	];

    // Function to generate excel with multiple tabs
	const generateExcel = (data1, data2) => {
		try {
			const wb = XLSX.utils.book_new();
	
			const ws1 = XLSX.utils.aoa_to_sheet(
				[
					sheetHeader,
					...data1.map((item) => [
						item.projectCode || poFirstApproverName?.type,
						item.projectType,
						item.itemName,
						item.requesterName,
						item.firstApproverName || poFirstApproverName?.firstApprover,
						item.finalApproverName || item.openPOApproverNameFinal,
						item.vendorName,
						item.originallyRequestedQty,
						item.originallyRequestedRate,
						item.originallyRequestedValue,
						item.standardQuantity,
						item.standardRate,
						item.standardValue,
						item.approvedQuantity,
						item.approvedRate,
						item.approvedValue,
						item.impactQuantity,
						item.impactRate,
						item.impactValue,
						item.updatedAt,
					]),
				]
				// data1
			);
			XLSX.utils.book_append_sheet(wb, ws1, "Positive Impact");
	
			const ws2 = XLSX.utils.aoa_to_sheet([
				sheetHeader,
				...data2.map((item) => [
					item.projectCode || poFirstApproverName?.type,
					item.projectType,
					item.itemName,
					item.requesterName,
					item.firstApproverName || poFirstApproverName?.firstApprover,
					item.finalApproverName || item.openPOApproverNameFinal,
					item.vendorName,
					item.originallyRequestedQty,
					item.originallyRequestedRate,
					item.originallyRequestedValue,
					item.standardQuantity,
					item.standardRate,
					item.standardValue,
					item.approvedQuantity,
					item.approvedRate,
					item.approvedValue,
					item.impactQuantity,
					item.impactRate,
					item.impactValue,
					item.updatedAt,
				]),
			]);
			XLSX.utils.book_append_sheet(wb, ws2, "Negative Impact");
	
			const wbout = XLSX.write(wb, { type: "binary", bookType: "xlsx" });
	
			const s2ab = (s) => {
				const buf = new ArrayBuffer(s.length);
				const view = new Uint8Array(buf);
				for (let i = 0; i !== s.length; ++i) view[i] = s.charCodeAt(i) & 0xff;
				return buf;
			};
	
			const blob = new Blob([s2ab(wbout)], {
				type: "application/octet-stream",
			});
			const url = URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.setAttribute("download", "Standard_Rate_Approval_Report.xlsx");
			document.body.appendChild(link);
			link.click();
		} catch (error) {
			console.error(error);
			props.setSnackInfo("Something went wrong", "error");
		}
	};

	// Function to handle the radio Change for PO & SO
	const handleRadioChange = (event) => {
        setStartDate("");
		setEndDate("");
		setRadioValue(event.target.value);
	};

	// getting procurements order details approved by operation head
	const getAllProcurementsApprovedByOperationHead = async () => {
		props.setLoading(true);
		try {
			const response =
				await HttpService.getAllProcurementsDetailsApprovedByOperationHead();
			// setPoSoApproved(response.data);
			// setDisplayData(response.data);
			let arr = response.data
                .filter((el) => el.approvedRate > el.standardRate)
				.sort((a, b) => {
					const dateA = moment(a.updatedAt, "DD/MM/YYYY", true);
					const dateB = moment(b.updatedAt, "DD/MM/YYYY", true);
					if (dateA.isValid() && dateB.isValid()) {
						return dateB.toDate() - dateA.toDate();
					} else {
						return 0; // Handle invalid dates as needed
					}
				});
			setDisplayData(arr);
		} catch (error) {
			console.error(error);
			props.setSnackInfo("Couldn't get raised po details", "error");
		}
		props.setLoading(false);
	};

	// getting service order details approved by operation head
	const getServiceOrderDetailsApprovedByOperationHead = async () => {
		props.setLoading(true);
		try {
			const response =
				await HttpService.getServiceOrderDetailsApprovedByOperationHead();
            // setPoSoApproved(response.data);
			let arr = response.data
                .filter((el) => el.approvedRate > el.standardRate)
				.sort((a, b) => {
					const dateA = moment(a.updatedAt, "DD/MM/YYYY", true);
					const dateB = moment(b.updatedAt, "DD/MM/YYYY", true);
					if (dateA.isValid() && dateB.isValid()) {
						return dateB.toDate() - dateA.toDate();
					} else {
						return 0; // Handle invalid dates as needed
					}
				});
			setDisplayData(arr);
		} catch (error) {
			console.error(error);
			props.setSnackInfo("Couldn't get raised po details", "error");
		}
		props.setLoading(false);
	};

    // getting service order details and procurement details approved by operation head
	const getSoPoApprovedByOperationHead = async () => {
		props.setLoading(true);
		try {
			const soApproved =
				await HttpService.getServiceOrderDetailsApprovedByOperationHead();
			const poApproved =
				await HttpService.getAllProcurementsDetailsApprovedByOperationHead();
			const newArray = await soApproved?.data.concat(poApproved?.data);
			setPoSoApproved(newArray);
			let arr = newArray
                .filter((el) => el.approvedRate > el.standardRate)
				.sort((a, b) => {
					const dateA = moment(a.updatedAt, "DD/MM/YYYY", true);
					const dateB = moment(b.updatedAt, "DD/MM/YYYY", true);
					if (dateA.isValid() && dateB.isValid()) {
						return dateB.toDate() - dateA.toDate();
					} else {
						return 0; // Handle invalid dates as needed
					}
				});
			setDisplayData(arr);
		} catch (error) {
			console.error("Error - ", error);
		}
		props.setLoading(false);
	};

	// Function to download csv file
	const csvDownloadHandler = () => {
        props.setLoading(true);
		try {
			const negativeImpactArray = [];
			const positiveImpactArray = [];
			// const csvArray = displayData.map((el) => ({
			// 	...el,
			// 	updatedAt: new Date(el.updatedAt).toLocaleDateString(),
			// }));
			for (let i = 0; i < poSoApproved.length; i++) {
				if (poSoApproved[i].approvedRate < poSoApproved[i].standardRate) {
					positiveImpactArray.push(poSoApproved[i]);
				} else if (
					poSoApproved[i].approvedRate > poSoApproved[i].standardRate
				) {
					negativeImpactArray.push(poSoApproved[i]);
				}
			}
			// setCsvData(csvArray);
            negativeImpactArray.sort((a, b) => {
				const dateA = moment(a.updatedAt, "DD/MM/YYYY", true);
				const dateB = moment(b.updatedAt, "DD/MM/YYYY", true);
				if (dateA.isValid() && dateB.isValid()) {
					return dateB.toDate() - dateA.toDate();
				} else {
					return 0; // Handle invalid dates as needed
				}
			});
			positiveImpactArray.sort((a, b) => {
				const dateA = moment(a.updatedAt, "DD/MM/YYYY", true);
				const dateB = moment(b.updatedAt, "DD/MM/YYYY", true);
				if (dateA.isValid() && dateB.isValid()) {
					return dateB.toDate() - dateA.toDate();
				} else {
					return 0; // Handle invalid dates as needed
				}
			});
			generateExcel(negativeImpactArray, positiveImpactArray);
			// downloadExcel();
		} catch (error) {
			console.error(error);
		}
        props.setLoading(false);
	};

	const downloadExcel = () => {
		setLoading(true);
		try {
			setTimeout(() => {
				csvButtonRef.current.link.click();
			});
		} catch (err) {
			console.error(err.message);
		}
		setLoading(false);
	};

    // Funciton to clear the date filter
	const clearDateFilterHandler = () => {
		props.setLoading(true);
		try {
			setStartDate("");
			setEndDate("");
            if (radioValue === "Both") {
				getSoPoApprovedByOperationHead();
			} else {
				setRadioValue("Both");
			}
		} catch (error) {
			console.error("Error ->", error);
		}
		props.setLoading(false);
	};

	// Function to filter data based on dates
	const handleDateFilter = () => {
		props.setLoading(true);
		try {
			const filteredArray = displayData.filter((el) => {
				let itemDate = moment(el?.updatedAt, "DD/MM/YYYY", true); // Parse the date using Moment.js
                if (!itemDate.isValid()) {
					console.error("Invalid date:", el?.updatedAt);
					return false; // Handle invalid dates as needed
				}

				let selectedStartDate = new Date(startDate);
				let selectedEndDate = new Date(endDate);

				// Adjust selectedEndDate to the end of the day
				selectedEndDate.setHours(23, 59, 59, 999);

				// Extract the date part only for comparison
				const itemDateWithoutTime = moment(itemDate).startOf("day");
				const selectedStartDateWithoutTime =
					moment(selectedStartDate).startOf("day");
				const selectedEndDateWithoutTime =
					moment(selectedEndDate).startOf("day");

                return (
                    itemDateWithoutTime >= selectedStartDateWithoutTime &&
                    itemDateWithoutTime <= selectedEndDateWithoutTime
                );
			});
			setDisplayData(filteredArray);
		} catch (error) {
			console.error("Error ->", error);
		}
		props.setLoading(false);
	};

	useEffect(() => {
		if (radioValue === "PO") {
			getAllProcurementsApprovedByOperationHead();
		} else if (radioValue === "SO") {
			getServiceOrderDetailsApprovedByOperationHead();
		} else if (radioValue === "Both") {
			getSoPoApprovedByOperationHead();
		}
	}, [radioValue]);

	return (
		<div>
			<div className={classes.report}>
				<Paper className={classes.paper}>
					<center>
						<h2>
                            {`${
								radioValue === "PO"
									? "Procurement"
									: radioValue === "SO"
									? "Service"
									: "Procurement & Service"
							}`}{" "}
							Order Report
						</h2>
					</center>
				</Paper>
			</div>
			<div style={{ alignSelf: "center" }}>
				<FormControl component="fieldset">
					<RadioGroup
						aria-label="type"
						name="type"
						value={radioValue}
						onChange={handleRadioChange}
					>
						<div className="modal-header-radio">
                            <FormControlLabel value="Both" control={<Radio />} label="Both" />
							<FormControlLabel value="PO" control={<Radio />} label="PO" />
							<FormControlLabel value="SO" control={<Radio />} label="SO" />
						</div>
						<div style={{ marginBottom: 10 }}>
							<CSVLink
								headers={headers}
								filename={
									`${
										radioValue === "PO" ? "Procurement" : "Service"
									} Order Report` + ".csv"
								}
								data={csvData}
								ref={csvButtonRef}
							/>
							<Button
								onClick={csvDownloadHandler}
								style={{
									marginLeft: 10,
									backgroundColor: "green",
									color: "white",
								}}
								variant="contained"
								color="success"
							>
								Download Impact CSV
							</Button>
						</div>
					</RadioGroup>
				</FormControl>
			</div>
            <Paper className={classes.date}>
				<Grid
					container
					style={{
						display: "flex",
						alignItems: "center",
						width: "70vw",
						marginLeft: 25,
					}}
				>
					<Grid item md={3} xs={12}>
						<TextField
							id="date"
							name="startDate"
							label="Start Date"
							type="date"
							value={startDate}
							onChange={(e) => setStartDate(e.target.value)}
							InputLabelProps={{
								shrink: true,
							}}
							fullWidth
							className={classes.textField}
						/>
					</Grid>
					<Grid item md={1} xs={12}></Grid>
					<Grid item md={3} xs={12}>
						<TextField
							id="date"
							name="endDate"
							label="End Date"
							type="date"
							value={endDate}
							onChange={(e) => setEndDate(e.target.value)}
							InputLabelProps={{
								shrink: true,
							}}
							fullWidth
							className={classes.textField}
						/>
					</Grid>
					<Grid item md={3} xs={12}>
						<Button
							variant="contained"
							color="primary"
							disabled={!startDate || !endDate}
							onClick={handleDateFilter}
							style={{ marginLeft: 10 }}
						>
							Submit
						</Button>
					</Grid>
					<Grid item md={2} xs={12}>
						<Button
							variant="contained"
							color="secondary"
							onClick={clearDateFilterHandler}
						>
							Clear Filter
						</Button>
					</Grid>
				</Grid>
			</Paper>
			<TableContainer component={Paper}>
				<Table
					className={classes.table}
					size="small"
					aria-label="customized table"
				>
					<TableHead>
						<TableRow>
							<StyledTableCell>Sl No</StyledTableCell>
							<StyledTableCell>Project No</StyledTableCell>
							<StyledTableCell>SO/PO</StyledTableCell>
							<StyledTableCell>Material Name</StyledTableCell>
                            <StyledTableCell>Requester Name</StyledTableCell>
							<StyledTableCell>First Approver Name</StyledTableCell>
							<StyledTableCell>Final Approver Name</StyledTableCell>
                            <StyledTableCell>Vendor Name</StyledTableCell>
							<StyledTableCell>Requested Qty</StyledTableCell>
							<StyledTableCell>Requested Rate</StyledTableCell>
							<StyledTableCell>Requested Value</StyledTableCell>
							<StyledTableCell>Standard Qty</StyledTableCell>
							<StyledTableCell>Standard Rate</StyledTableCell>
							<StyledTableCell>Standard Value</StyledTableCell>
							<StyledTableCell>Approved Qty</StyledTableCell>
							<StyledTableCell>Approved Rate</StyledTableCell>
							<StyledTableCell>Approved Value</StyledTableCell>
							<StyledTableCell>Impact Qty</StyledTableCell>
							<StyledTableCell>Impact Rate</StyledTableCell>
							<StyledTableCell>Impact Value</StyledTableCell>
                            <StyledTableCell>Approval Date</StyledTableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{displayData?.map((data, i) => (
							<StyledTableRow key={i + 1}>
								<StyledTableCell>{i + 1}</StyledTableCell>
								<StyledTableCell>{data?.projectCode || poFirstApproverName?.type || "N/A"}</StyledTableCell>
								<StyledTableCell>{data?.projectType || "N/A"}</StyledTableCell>
								<StyledTableCell>{data?.itemName || "N/A"}</StyledTableCell>
								<StyledTableCell>
									{data?.requesterName || "N/A"}
								</StyledTableCell>
								<StyledTableCell>
									{data?.firstApproverName || poFirstApproverName?.firstApprover || "N/A"}
								</StyledTableCell>
								<StyledTableCell>
									{data?.finalApproverName || data?.openPOApproverNameFinal || "N/A" }
								</StyledTableCell>
								<StyledTableCell>{data?.vendorName || "N/A"}</StyledTableCell>
								<StyledTableCell>
									{parseFloat(data?.originallyRequestedQty).toFixed(2)}
								</StyledTableCell>
								<StyledTableCell>
                                    {parseFloat(data?.originallyRequestedRate).toFixed(2)}
								</StyledTableCell>
								<StyledTableCell>
                                    {parseFloat(data?.originallyRequestedValue).toFixed(2)}
								</StyledTableCell>
								<StyledTableCell>
									{parseFloat(data?.standardQuantity).toFixed(2)}
								</StyledTableCell>
								<StyledTableCell>
									{parseFloat(data?.standardRate).toFixed(2)}
								</StyledTableCell>
								<StyledTableCell>
									{parseFloat(data?.standardValue).toFixed(2)}
								</StyledTableCell>
								<StyledTableCell>
									{parseFloat(data?.approvedQuantity).toFixed(2)}
								</StyledTableCell>
								<StyledTableCell>
									{parseFloat(data?.approvedRate).toFixed(2)}
								</StyledTableCell>
								<StyledTableCell>
									{parseFloat(data?.approvedValue).toFixed(2)}
								</StyledTableCell>
								<StyledTableCell>
                                    {isNaN(parseFloat(data?.impactQuantity).toFixed(2))
										? "N/A"
										: parseFloat(data?.impactQuantity).toFixed(2)}
								</StyledTableCell>
								<StyledTableCell>
									{parseFloat(data?.impactRate).toFixed(2)}
								</StyledTableCell>
								<StyledTableCell>
									{parseFloat(data?.impactValue).toFixed(2)}
								</StyledTableCell>
                                <StyledTableCell>
                                    {
										data?.updatedAt
										// ? new Date(data.updatedAt).toLocaleDateString()
										// : ""
									}
								</StyledTableCell>
							</StyledTableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>
		</div>
	);
};

export default connect(null, { setLoading, setSnackInfo })(Reports);